import { useState, useRef, useEffect, useCallback } from 'react';
import { useLiveQuery } from "dexie-react-hooks";
import { db } from "../db";
import { syncService } from "../services/SyncService";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash2, Edit2, AlertCircle, Check, ChevronsUpDown, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { toRupees, toPaise } from "../utils/currency";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { PhoneInput } from "@/components/PhoneInput";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { cn } from "@/lib/utils";
import PropTypes from 'prop-types';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

export function FeeAgreementManager({ userId, patientId, clinicId }) {
    const [showDialog, setShowDialog] = useState(false);
    const [editingRule, setEditingRule] = useState(null);
    const [formData, setFormData] = useState({
        name: "",
        rule_type: "percent",
        fee_value: "",
        clinic_id: "",           // NEW: The selected clinic ID for the fee agreement
        clinic_name: "",         // NEW: Name for a new stub clinic
        clinic_phone: "",        // NEW: Phone for a new stub clinic
        clinic_address: ""       // NEW: Address for a new stub clinic
    });

    // UI state
    const [openClinicSelect, setOpenClinicSelect] = useState(false);
    const [clinicConflict, setClinicConflict] = useState(null); // existing clinic matched by name+phone

    // Fetch all clinics the user has access to (must be before callbacks that use it)
    const clinics = useLiveQuery(() => db.clinics.toArray()) || [];

    // Backend search state
    const [dropdownSearchTerm, setDropdownSearchTerm] = useState('');
    const [dropdownSearchResults, setDropdownSearchResults] = useState([]);
    const [dropdownSearching, setDropdownSearching] = useState(false);
    // new-clinic search: idle | searching | found | not_found
    const [newClinicSearchState, setNewClinicSearchState] = useState('idle');
    const [newClinicSearchResults, setNewClinicSearchResults] = useState([]);
    const dropdownSearchTimer = useRef(null);
    const newClinicSearchTimer = useRef(null);

    const getAuthConfig = () => ({
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    });

    // Save a backend clinic to local Dexie if not already there,
    // and ensure the user is a member (needed for fee_agreement creation).
    const saveClinicLocally = useCallback(async (clinic) => {
        const existing = await db.clinics.get(clinic.id);
        if (!existing) {
            await db.clinics.add({ ...clinic, _synced: true });
        }
        // Ensure the user is a member of this clinic so fee_agreement creation succeeds.
        // Add membership locally + queue sync; queue is FIFO so this runs before fee_agreement.
        const memberId = `mem_${clinic.id}_${userId}`;
        const existingMember = await db.clinic_members.get(memberId);
        if (!existingMember) {
            const newMember = {
                id: memberId,
                clinic_id: clinic.id,
                user_id: userId,
                role: 'doctor',
                is_active: true,
                created_at: new Date().toISOString(),
                _synced: false
            };
            await db.clinic_members.add(newMember);
            await syncService.addToQueue('create', 'clinic_member', newMember);
        }
    }, [userId]);

    // Debounced backend search for the dropdown combobox
    const handleDropdownSearch = useCallback((term) => {
        setDropdownSearchTerm(term);
        if (dropdownSearchTimer.current) clearTimeout(dropdownSearchTimer.current);
        if (!term || term.length < 2) { setDropdownSearchResults([]); return; }
        dropdownSearchTimer.current = setTimeout(async () => {
            setDropdownSearching(true);
            try {
                const res = await axios.get(`${API_URL}/api/clinics/search?name=${encodeURIComponent(term)}&is_placeholder=true`, getAuthConfig());
                // Filter out clinics already in local list to avoid duplication
                const localIds = new Set(clinics.map(c => c.id));
                setDropdownSearchResults(res.data.filter(c => !localIds.has(c.id)));
            } catch (e) {
                console.error('Clinic search failed:', e);
            } finally {
                setDropdownSearching(false);
            }
        }, 400);
    }, [clinics]);

    // PhoneInput returns countryCode+number e.g. "919876543210" — extract the last 10 digits
    const extractLocalPhone = (val) => {
        if (!val) return '';
        const digits = val.replace(/\D/g, '');
        return digits.length >= 10 ? digits.slice(-10) : digits;
    };

    // Debounced backend search for name+phone new clinic fields
    const triggerNewClinicBackendSearch = useCallback((name, rawPhone) => {
        if (newClinicSearchTimer.current) clearTimeout(newClinicSearchTimer.current);
        setNewClinicSearchResults([]);
        if (!name || name.length < 2) {
            setNewClinicSearchState('idle');
            return;
        }
        const localPhone = extractLocalPhone(rawPhone);
        const hasFullPhone = localPhone.length === 10;
        setNewClinicSearchState('searching');
        newClinicSearchTimer.current = setTimeout(async () => {
            try {
                const params = new URLSearchParams({ name, is_placeholder: 'true' });
                if (hasFullPhone) params.set('phone', localPhone);
                const res = await axios.get(`${API_URL}/api/clinics/search?${params}`, getAuthConfig());
                const results = res.data;
                setNewClinicSearchResults(results);
                if (results.length === 0) {
                    setNewClinicSearchState('not_found');
                } else {
                    // Check for exact match (name + phone) → auto-select
                    const exact = hasFullPhone ? results.find(
                        c => c.name?.toLowerCase() === name.trim().toLowerCase() && c.phone === localPhone
                    ) : null;
                    if (exact) {
                        await saveClinicLocally(exact);
                        setClinicConflict(exact);
                        setFormData(prev => ({ ...prev, clinic_id: exact.id, clinic_name: '', clinic_phone: '', clinic_address: '' }));
                        setNewClinicSearchResults([]);
                        setNewClinicSearchState('idle');
                    } else {
                        setNewClinicSearchState('found');
                    }
                }
            } catch (e) {
                console.error('New clinic backend search failed:', e);
                setNewClinicSearchState('idle');
            }
        }, 500);
    }, [saveClinicLocally]);

    // Cleanup timers
    useEffect(() => () => {
        if (dropdownSearchTimer.current) clearTimeout(dropdownSearchTimer.current);
        if (newClinicSearchTimer.current) clearTimeout(newClinicSearchTimer.current);
    }, []);

    // Check if a new clinic name+phone combo already exists (case-insensitive) in local db
    const checkClinicConflict = (name, rawPhone, currentClinics) => {
        if (!name) return null;
        const localPhone = extractLocalPhone(rawPhone);
        if (localPhone.length !== 10) return null;
        const nameLower = name.trim().toLowerCase();
        return currentClinics.find(
            c => c.name?.toLowerCase() === nameLower && c.phone === localPhone
        ) || null;
    };

    // Compute whether Save should be enabled
    const isSaveEnabled = (() => {
        const hasValue = formData.fee_value !== '' && Number(formData.fee_value) >= 0;
        if (!hasValue) return false;
        if (clinicId) return true;
        if (formData.clinic_id) return true;
        if (!formData.clinic_id && formData.clinic_name && extractLocalPhone(formData.clinic_phone).length === 10) {
            return newClinicSearchState === 'not_found';
        }
        return false;
    })();


    const rules = useLiveQuery(async () => {
        // Use compound index for better reactivity
        // When patientId is provided, use [user_id+patient_id] compound index
        if (patientId) {
            // 1. Get personal rules
            // Fetch rules for this user and filter by patientId in memory
            // This avoids potential compound index issues while remaining efficient
            const personalRules = await db.fee_agreements
                .where('user_id')
                .equals(userId)
                .filter(r => {
                    const pIdMatch = r.patient_id === patientId;
                    const clinicMatch = clinicId ? r.clinic_id === clinicId : true;
                    return pIdMatch && clinicMatch && r.is_active !== false;
                })
                .toArray();

            // 2. Get clinic default rules (only if patient has appointments in that clinic)
            // We need to know which clinics the patient has visited
            const patientAppointments = await db.appointments
                .where('patient_id').equals(patientId)
                .toArray();

            const visitedClinicIds = new Set(patientAppointments.map(a => a.clinic_id).filter(id => id));

            // If we are passing a specific clinicId, we check only that
            const targetClinicIds = clinicId ? [clinicId] : Array.from(visitedClinicIds);

            let clinicRules = [];
            if (targetClinicIds.length > 0) {
                const allClinicRules = await db.fee_agreements
                    .where('user_id').equals(userId)
                    .toArray();

                clinicRules = allClinicRules.filter(r =>
                    targetClinicIds.includes(r.clinic_id) && !r.patient_id && r.is_active !== false
                );
            }

            return [...personalRules, ...clinicRules];
        }

        // When only clinicId is provided (Settings view)
        if (clinicId) {
            return await db.fee_agreements
                .where('user_id').equals(userId)
                .filter(r => r.clinic_id === clinicId && !r.patient_id && r.is_active !== false)
                .toArray();
        }

        // Default: all rules for this user (for general settings view)
        return await db.fee_agreements
            .where('user_id').equals(userId)
            .filter(r => r.is_active !== false)
            .toArray();
    }, [userId, patientId, clinicId]) || [];

    // Check if we already have a specific rule for this patient
    const hasPersonalRule = rules.some(r => r.patient_id === patientId);

    const referralDoctors = useLiveQuery(() => db.users.toArray().then(users => users.filter(u => u.id !== userId))) || [];


    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            // Validation: Must select or create a clinic
            if (!clinicId && !formData.clinic_id && !formData.clinic_name) {
                toast.error("Please select a clinic or provide a name for a new one.");
                return;
            }
            // Convert to paise if flat amount
            const value = parseFloat(formData.fee_value);
            const finalValue = formData.rule_type === 'flat' ? toPaise(value) : value;


            let finalClinicId = clinicId || formData.clinic_id;

            // Create new stub clinic if needed
            if (!finalClinicId && formData.clinic_name) {
                if (!formData.clinic_phone) {
                    toast.error("Phone number is required when creating a new clinic.");
                    return;
                }
                finalClinicId = crypto.randomUUID();
                const newClinic = {
                    id: finalClinicId,
                    name: formData.clinic_name,
                    phone: formData.clinic_phone || "",
                    address: formData.clinic_address || "",
                    email: "",
                    owner_doctor_id: userId,
                    is_placeholder: true,
                    created_by_user_id: userId,
                    created_at: new Date().toISOString(),
                    updated_at: new Date().toISOString(),
                    _synced: false
                };
                await db.clinics.add(newClinic);
                await syncService.addToQueue('create', 'clinic', newClinic);

                // Optimistically add the member so it appears in dropdowns immediately
                const newMember = {
                    id: `mem_${finalClinicId}_${userId}`,
                    clinic_id: finalClinicId,
                    user_id: userId,
                    role: 'doctor', // Creator of placeholder gets 'doctor' role
                    is_active: true,
                    created_at: new Date().toISOString(),
                    _synced: false
                };
                await db.clinic_members.add(newMember);
            }

            const ruleData = {
                name: formData.name || null,
                rule_type: formData.rule_type,
                fee_value: finalValue,
                patient_id: patientId || null,
                clinic_id: finalClinicId,
                user_id: userId,
                status: "active",
                arrangement_type: "facility_fee",
                updated_at: new Date().toISOString()
            };

            if (editingRule) {
                await db.fee_agreements.update(editingRule.id, ruleData);
                await syncService.addToQueue('update', 'fee_agreement', { ...ruleData, id: editingRule.id });
                toast.success("Agreement updated");
            } else {
                const newRule = {
                    ...ruleData,
                    id: crypto.randomUUID(),
                    created_at: new Date().toISOString(),
                    _synced: false
                };
                await db.fee_agreements.add(newRule);
                await syncService.addToQueue('create', 'fee_agreement', newRule);
                toast.success("Agreement created");
            }
            setShowDialog(false);
            setEditingRule(null);
            setFormData({
                name: "", rule_type: "percent", fee_value: "",
                clinic_id: "", clinic_name: "", clinic_phone: "", clinic_address: ""
            });
        } catch (error) {
            console.error(error);
            toast.error("Failed to save rule");
        }
    };

    const handleDelete = async (id) => {
        if (!confirm("Delete this rule?")) return;
        try {
            await db.fee_agreements.delete(id);
            await syncService.addToQueue('delete', 'fee_agreement', { id });
            toast.success("Agreement deleted");
        } catch (error) {
            console.error(error);
            toast.error("Failed to delete rule");
        }
    };

    const openEdit = (rule) => {
        setEditingRule(rule);
        // Convert back to rupees for display if flat
        const displayValue = rule.rule_type === 'flat' ? toRupees(rule.fee_value) : rule.fee_value;

        setFormData({
            name: rule.name || "",
            rule_type: rule.rule_type,
            fee_value: displayValue,
            clinic_id: rule.clinic_id || "",
            clinic_name: "",
            clinic_phone: "",
            clinic_address: ""
        });
        setShowDialog(true);
    };

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium">Fee Agreements</h3>
                {!hasPersonalRule && patientId && (
                    <Button onClick={() => {
                        setEditingRule(null);
                        setFormData({
                            name: "", rule_type: "percent", fee_value: "",
                            clinic_id: "", clinic_name: "", clinic_phone: "", clinic_address: ""
                        });
                        setShowDialog(true);
                    }} size="sm">
                        <Plus className="w-4 h-4 mr-2" /> Add Agreement
                    </Button>
                )}
                {!patientId && (
                    <Button onClick={() => {
                        setEditingRule(null);
                        setFormData({
                            name: "", rule_type: "percent", fee_value: "",
                            clinic_id: "", clinic_name: "", clinic_phone: "", clinic_address: ""
                        });
                        setShowDialog(true);
                    }} size="sm">
                        <Plus className="w-4 h-4 mr-2" /> Add Agreement
                    </Button>
                )}
            </div>

            <div className="border rounded-md overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Clinic / Doctor</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead>Value</TableHead>
                            <TableHead className="w-[100px]">Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {rules.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={4} className="text-center text-muted-foreground">
                                    No specific agreements found.
                                </TableCell>
                            </TableRow>
                        ) : (
                            rules.map(rule => {
                                const assocDoc = referralDoctors.find(d => d.id === rule.clinic_id); // Try clinic owner lookup
                                const clinic = clinics.find(c => c.id === rule.clinic_id);
                                return (
                                    <TableRow key={rule.id} className={rule.sync_status === 'failed' ? 'bg-red-50' : ''}>
                                        <TableCell>
                                            <div className="flex flex-col">
                                                <div className="flex items-center gap-2 font-medium">
                                                    {assocDoc?.name || clinic?.name || "Clinic Rule"}
                                                    {rule.sync_status === 'failed' && (
                                                        <div title={rule.sync_error || 'Sync failed'} className="text-destructive cursor-help">
                                                            <AlertCircle className="w-4 h-4" />
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                                                    <span className="inline-block w-2 h-2 rounded-full bg-slate-300"></span>
                                                    {clinic?.name ? (clinic.name + " " + (clinic.is_placeholder ? '(Stub)' : '') + " ") : "All Clinics (Legacy)"}
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell className="capitalize">{rule.rule_type}</TableCell>
                                        <TableCell>{rule.rule_type === 'percent' ? (rule.fee_value + "% ") : ("₹" + toRupees(rule.fee_value) + " ")}</TableCell>
                                        <TableCell>
                                            <div className="flex gap-2">
                                                <Button variant="ghost" size="icon" onClick={() => openEdit(rule)}>
                                                    <Edit2 className="w-4 h-4" />
                                                </Button>
                                                <Button variant="ghost" size="icon" onClick={() => handleDelete(rule.id)}>
                                                    <Trash2 className="w-4 h-4 text-destructive" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </div>

            <Dialog open={showDialog} onOpenChange={setShowDialog}>
                <DialogContent className="sm:max-w-[500px]">
                    <DialogHeader>
                        <DialogTitle>{editingRule ? 'Edit Agreement' : 'New Agreement'}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4 max-h-[80vh] overflow-y-auto pr-2">

                        {!clinicId && (
                            <div className="space-y-4 border p-4 rounded-md bg-slate-50">
                                <h4 className="text-sm font-medium">Select or Create Clinic</h4>
                                <Popover open={openClinicSelect} onOpenChange={setOpenClinicSelect}>
                                    <PopoverTrigger asChild>
                                        <Button
                                            variant="outline"
                                            role="combobox"
                                            aria-expanded={openClinicSelect}
                                            className="w-full justify-between"
                                        >
                                            {formData.clinic_id
                                                ? clinics.find((c) => c.id === formData.clinic_id)?.name
                                                : "Select a clinic..."}
                                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-full p-0">
                                        <Command filter={(value, search) => {
                                            if (value.toLowerCase().includes(search.toLowerCase())) return 1;
                                            return 0;
                                        }}>
                                            <CommandInput
                                                placeholder="Search clinics..."
                                                onValueChange={handleDropdownSearch}
                                            />
                                            <CommandList>
                                                {dropdownSearching && (
                                                    <div className="flex items-center justify-center py-2 text-xs text-muted-foreground gap-1">
                                                        <Loader2 className="h-3 w-3 animate-spin" /> Searching...
                                                    </div>
                                                )}
                                                <CommandEmpty>{dropdownSearchTerm.length >= 2 && !dropdownSearching ? 'No clinics found.' : 'Type to search...'}</CommandEmpty>
                                                <CommandGroup heading="Local">
                                                    {clinics.map((clinic) => (
                                                        <CommandItem
                                                            key={clinic.id}
                                                            value={clinic.name}
                                                            onSelect={async () => {
                                                                setFormData({ ...formData, clinic_id: clinic.id, clinic_name: "", clinic_phone: "", clinic_address: "" });
                                                                setClinicConflict(null);
                                                                setOpenClinicSelect(false);
                                                            }}
                                                        >
                                                            <Check className={cn("mr-2 h-4 w-4", formData.clinic_id === clinic.id ? "opacity-100" : "opacity-0")} />
                                                            {clinic.name} {clinic.is_placeholder && "(Stub)"}
                                                        </CommandItem>
                                                    ))}
                                                </CommandGroup>
                                                {dropdownSearchResults.length > 0 && (
                                                    <CommandGroup heading="From Server">
                                                        {dropdownSearchResults.map((clinic) => (
                                                            <CommandItem
                                                                key={clinic.id}
                                                                value={clinic.name}
                                                                onSelect={async () => {
                                                                    await saveClinicLocally(clinic);
                                                                    setFormData({ ...formData, clinic_id: clinic.id, clinic_name: "", clinic_phone: "", clinic_address: "" });
                                                                    setClinicConflict(null);
                                                                    setDropdownSearchResults([]);
                                                                    setOpenClinicSelect(false);
                                                                }}
                                                            >
                                                                <Check className={cn("mr-2 h-4 w-4", formData.clinic_id === clinic.id ? "opacity-100" : "opacity-0")} />
                                                                {clinic.name} {clinic.phone && <span className="text-xs text-muted-foreground ml-1">{clinic.phone}</span>}
                                                            </CommandItem>
                                                        ))}
                                                    </CommandGroup>
                                                )}
                                            </CommandList>
                                        </Command>
                                    </PopoverContent>
                                </Popover>

                                {formData.clinic_id && (
                                    <div className="space-y-1">
                                        {clinicConflict && (
                                            <div className="flex items-center gap-2 text-xs text-green-700 bg-green-50 border border-green-200 rounded px-2 py-1">
                                                <Check className="h-3 w-3 shrink-0" />
                                                Existing clinic &quot;{clinicConflict.name}&quot; auto-selected (name + phone matched)
                                            </div>
                                        )}
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            className="mt-1 h-auto p-0 text-xs text-muted-foreground hover:text-destructive"
                                            onClick={() => { setFormData(prev => ({ ...prev, clinic_id: '' })); setClinicConflict(null); }}
                                        >
                                            <X className="mr-1 h-3 w-3 inline" /> Clear Selection
                                        </Button>
                                    </div>
                                )}

                                {!formData.clinic_id && (
                                    <>
                                        <div className="text-center text-xs text-muted-foreground my-2">OR ADD NEW STUB CLINIC</div>
                                        <div className="space-y-3">
                                            <div>
                                                <Label>Clinic Name</Label>
                                                <Input
                                                    placeholder="Enter clinic name"
                                                    value={formData.clinic_name}
                                                    onChange={e => {
                                                        const newName = e.target.value;
                                                        const conflict = checkClinicConflict(newName, formData.clinic_phone, clinics);
                                                        setClinicConflict(conflict);
                                                        if (conflict) {
                                                            setFormData(prev => ({ ...prev, clinic_name: newName, clinic_id: conflict.id, clinic_phone: '', clinic_address: '' }));
                                                        } else {
                                                            setFormData(prev => ({ ...prev, clinic_name: newName }));
                                                            triggerNewClinicBackendSearch(newName, formData.clinic_phone);
                                                        }
                                                    }}
                                                />
                                            </div>
                                            <div>
                                                <Label>Phone *</Label>
                                                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                                    <PhoneInput
                                                        id="clinic_phone"
                                                        placeholder="Clinic phone"
                                                        value={formData.clinic_phone}
                                                        onChange={val => {
                                                            const conflict = checkClinicConflict(formData.clinic_name, val, clinics);
                                                            setClinicConflict(conflict);
                                                            if (conflict) {
                                                                setFormData(prev => ({ ...prev, clinic_phone: val, clinic_id: conflict.id, clinic_name: '', clinic_address: '' }));
                                                            } else {
                                                                setFormData(prev => ({ ...prev, clinic_phone: val }));
                                                                triggerNewClinicBackendSearch(formData.clinic_name, val);
                                                            }
                                                        }}
                                                        required
                                                        style={{ flex: 1 }}
                                                    />
                                                    {'contacts' in navigator && (
                                                        <Button
                                                            type="button"
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={async () => {
                                                                try {
                                                                    const contacts = await navigator.contacts.select(['tel', 'name'], { multiple: false });
                                                                    if (contacts && contacts.length > 0 && contacts[0].tel && contacts[0].tel.length > 0) {
                                                                        let phone = contacts[0].tel[0].replace(/\D/g, ''); // Remove non-digits

                                                                        // Strip leading 91 or 0 if length > 10
                                                                        if (phone.length > 10 && phone.startsWith('91')) {
                                                                            phone = phone.slice(phone.length - 10);
                                                                        } else if (phone.length > 10 && phone.startsWith('0')) {
                                                                            phone = phone.slice(phone.length - 10);
                                                                        }

                                                                        if (phone.length !== 10) {
                                                                            toast.error('Selected phone number must be 10 digits.');
                                                                        } else {
                                                                            const newName = (!formData.clinic_name && contacts[0].name && contacts[0].name.length > 0) ? contacts[0].name[0] : formData.clinic_name;
                                                                            setFormData(prev => ({ ...prev, clinic_phone: phone, clinic_name: newName }));
                                                                        }
                                                                    }
                                                                } catch (error) {
                                                                    console.error('Contact selection failed:', error);
                                                                }
                                                            }}
                                                            style={{ minWidth: '40px', padding: '0 0.5rem' }}
                                                        >
                                                            📇
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Backend search feedback */}
                                        {newClinicSearchState === 'searching' && (
                                            <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                                                <Loader2 className="h-3 w-3 animate-spin" /> Checking server for duplicates...
                                            </div>
                                        )}
                                        {newClinicSearchState === 'not_found' && (
                                            <div className="flex items-center gap-1 text-xs text-green-700 mt-1">
                                                <Check className="h-3 w-3" /> No existing clinic found — you can save to create a new one.
                                            </div>
                                        )}
                                        {newClinicSearchState === 'found' && newClinicSearchResults.length > 0 && (
                                            <div className="border rounded-md bg-white shadow-sm mt-1">
                                                <div className="text-xs text-amber-700 bg-amber-50 px-2 py-1 border-b flex items-center gap-1">
                                                    <AlertCircle className="h-3 w-3" /> Existing clinics found — select one or clear name/phone to create new:
                                                </div>
                                                {newClinicSearchResults.map(c => (
                                                    <button
                                                        key={c.id}
                                                        type="button"
                                                        className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex justify-between items-center"
                                                        onClick={async () => {
                                                            await saveClinicLocally(c);
                                                            setClinicConflict(c);
                                                            setFormData(prev => ({ ...prev, clinic_id: c.id, clinic_name: '', clinic_phone: '', clinic_address: '' }));
                                                            setNewClinicSearchResults([]);
                                                            setNewClinicSearchState('idle');
                                                        }}
                                                    >
                                                        <span className="font-medium">{c.name}</span>
                                                        <span className="text-xs text-muted-foreground">{c.phone}</span>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        )}



                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Type</Label>
                                <Select
                                    value={formData.rule_type}
                                    onValueChange={(val) => setFormData({ ...formData, rule_type: val })}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="percent">Percentage (%)</SelectItem>
                                        <SelectItem value="flat">Flat Amount (₹)</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Value</Label>
                                <Input
                                    type="number"
                                    step="1"
                                    min="0"
                                    value={formData.fee_value}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (val === '' || /^\d+$/.test(val)) {
                                            setFormData({ ...formData, fee_value: val });
                                        }
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === '.' || e.key === '-' || e.key === 'e') {
                                            e.preventDefault();
                                        }
                                    }}
                                    required
                                />
                            </div>
                        </div>

                        <Button type="submit" className="w-full" disabled={!isSaveEnabled}>Save Agreement</Button>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}

FeeAgreementManager.propTypes = {
    userId: PropTypes.string,
    patientId: PropTypes.string,
    clinicId: PropTypes.string
};
